import { ActionConfirmation } from 'Components/campaigns/applications/ApplicationCard'
import CloseButton from 'Components/Common/CloseButton'
import InstagramCaption from 'Components/Common/InstagramCaption'
import { PRIVILEGE } from 'constants/privilege'
import { usePrivileges } from 'hooks/useAuth'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Modal, Spinner } from 'reactstrap'
import { processCollaboration as processCollaborationAPI, processCollaborationMilestoneMedia } from 'services/api/campaign/collaboration'
import { updatePostStatus } from 'slices/campaigns/collaborations/reducer'
import { fetchPost, fetchSocialPostThunk } from 'slices/influencer/thunk'
import { closeInfluencerPostModal } from 'slices/ui/reducer'
import { AppDispatch, RootState, store } from 'store'
import { Influencer, SocialAccount } from 'types/influencer'
import { getErrorMessage } from 'utils/helpers'
import PerfectScrollbar from 'react-perfect-scrollbar'
import userProfilePicture from "../../assets/images/users/user-dummy-img.jpg"
import { fetchCollaborationPostThunk } from 'slices/campaigns/collaborations/milestones/thunk'

interface InfluencerPostModalProps {
    isOpen: boolean,
    onClose: (processed?: boolean) => void,
    influencer?: Influencer,
    socialAccount: SocialAccount,
    postId: string | null,
    status?: 'pending' | 'approved' | 'rejected' | 'processing',
    collaborationId?: string,
    milestoneStep?: string | null
    preventAction?: boolean,
}

const InfluencerPostModal: React.FC<InfluencerPostModalProps> = ({ isOpen, onClose, postId, socialAccount, influencer, status, milestoneStep = null, preventAction = false, collaborationId }) => {

    const { campaignId, milestoneId } = useParams();

    const dispatch: AppDispatch = useDispatch();

    const privileges = usePrivileges();

    const collaborationPost = useSelector((state: RootState) => state.collaborations.collaborationPosts[collaborationId!]?.[postId!]);
    const post = collaborationPost?.social_post;
    const postLoading = useSelector((state: RootState) => state.collaborations.collaborationPostLoading);
    const [postStatus, setPostStatus] = useState<'pending' | 'approved' | 'rejected' | 'processing' | undefined>(status);

    const [actionConfirmation, setActionConfirmation] = useState<null | ActionConfirmation>(null);

    useEffect(() => {
        setPostStatus(status)
    }, [status])

    // useEffect(() => {
    //     if (postId && socialAccount) {
    //         dispatch(fetchSocialPostThunk({ socialAccountId: socialAccount.uuid, postId }));
    //     }
    // }, [dispatch, postId, socialAccount])

    useEffect(() => {
        if (postId && collaborationId) {
            setActionConfirmation(null);
            dispatch(fetchCollaborationPostThunk({ collaborationId, postId }));
        }
    }, [dispatch, postId, collaborationId])

    const onModalClose = (processed = false) => {
        dispatch(closeInfluencerPostModal());
        onClose(processed);
    }

    const processCollaboration = async (influencerId: string, action: 'accept' | 'reject') => {
        if (!campaignId) return;
        if (!privileges.includes(PRIVILEGE.CAMPAIGN.APPLICATION.PROCESS)) {
            toast.error("You don't have permission to process application!");
            return;
        }
        try {
            setPostStatus('processing');
            const agencyId = store.getState().auth.selectedAgencyId;
            if (!agencyId) throw new Error("Agency not selected!");
            const result = await processCollaborationAPI(agencyId, campaignId, influencerId, { action, social_post_id: postId });
            if (result.status && result.collaboration?.status) {
                setPostStatus(result.collaboration?.status)
                dispatch(updatePostStatus({ campaignId, collaborationId: influencerId, postId, status: result.collaboration?.status }));
                onModalClose();
            }

        } catch (error) {
            toast.error(getErrorMessage(error, "Failed to process collaboration!"));
            setPostStatus('pending');
            console.log(error);
        }
    }

    const processCollaborationMilestone = async (influencerId: string, action: 'accept' | 'reject') => {
        if (!campaignId || !milestoneId || !milestoneStep || !postId) return;
        if (!privileges.includes(PRIVILEGE.CAMPAIGN.APPLICATION.PROCESS)) {
            toast.error("You don't have permission to process application!");
            return;
        }
        try {
            setPostStatus('processing');
            const agencyId = store.getState().auth.selectedAgencyId;
            if (!agencyId) throw new Error("Agency not selected!");
            const result = await processCollaborationMilestoneMedia(agencyId, campaignId, influencerId, milestoneId,
                {
                    step: milestoneStep,
                    action,
                    social_post_id: postId
                }
            );
            if (result.status) {
                const status = action === 'accept' ? 'approved' : 'rejected';
                setPostStatus(status)
                dispatch(updatePostStatus({ campaignId, collaborationId: influencerId, postId, status }));
                onModalClose(true);
            }

        } catch (error) {
            toast.error(getErrorMessage(error, "Failed to process collaboration!"));
            setPostStatus('pending');
            console.log(error);
        }
    }

    const approve = async () => {
        if(!influencer) return;
        if (milestoneId && milestoneStep) {
            return processCollaborationMilestone(influencer.uuid, 'accept');
        }

        processCollaboration(influencer.uuid, 'accept');
    }

    const reject = async () => {
        if(!influencer) return;
        if (milestoneId && milestoneStep) {
            return processCollaborationMilestone(influencer.uuid, 'reject');
        }
        processCollaboration(influencer.uuid, 'reject');
    }

    return (
        <Modal isOpen={isOpen} toggle={() => onModalClose()} size='lg' className='post-modal'>
            <CloseButton top='0' onClose={onModalClose} />
            {
                postId && socialAccount && post &&
                <div className="d-flex flex-column flex-lg-row gap-2 gap-lg-0">
                    {
                        //NOTE: post = collaborationPost?.social_post
                        post.platform === 'ig' && post.ig &&
                        <>
                            {
                                (collaborationPost?.media_url || post.ig?.media_url) ? (
                                    <>
                                        {
                                            post?.ig?.media_type === 'VIDEO' &&
                                            <video src={collaborationPost?.media_url || post.ig?.media_url} controls autoPlay className='post' controlsList='nodownload nofullscreen noremoteplayback' />
                                        }
                                        {
                                            (post?.ig?.media_type === 'IMAGE' || post?.ig?.media_type === 'CAROUSEL_ALBUM') &&
                                            <img src={collaborationPost?.media_url || post.ig?.media_url} className='post' />
                                        }
                                    </>
                                ) : (
                                    <>
                                        <div className='no-media-cta'>
                                            <button className='btn btn-gradient gap-2'><span className='mdi mdi-open-in-new fs-4'></span>View on Instagram</button>
                                        </div>
                                    </>
                                )
                            }
                        </>
                    }
                    {
                        post.platform === 'yt' && post.yt &&
                        <>
                            {
                                post?.type === 'VIDEO' &&
                                <iframe
                                    src={post.yt.permalink ? `https://www.youtube.com/embed/${post.yt.permalink.split('/').pop()?.split('?')[0]}` : ""}
                                    className="mw-100 mh-800px align-self-center"
                                    style={{ objectFit: "contain", width: '100%', height: '740px', maxHeight: '75vh' }}
                                    frameBorder="0"
                                    allowFullScreen
                                    title="YouTube video"
                                />
                            }
                        </>
                    }

                    {
                        postStatus === 'approved' && <span className="post-status-badge approved"> <i className="bx bx-check"></i>Approved</span>
                    }
                    {
                        postStatus === 'pending' && <span className="post-status-badge new">New</span>
                    }
                    {
                        postStatus === 'rejected' && <span className="post-status-badge declined"> <i className="bx bx-x"></i>Declined</span>
                    }
                    <div className="post-details">
                        <PerfectScrollbar>
                            <div className='d-flex flex-column p-4 gap-4 mb-5'>
                                <div className="post-profile d-flex gap-3">
                                    <img src={socialAccount?.profile_picture_url ?? userProfilePicture} className="post-influencer-pic" />
                                    <div className="name-and-date d-flex flex-column align-items-start ">
                                        <span className="influencer-name">{influencer?.name ?? (socialAccount?.name ?? `@${socialAccount?.handle}`)}</span>
                                        <span className='influencer-address'>{post?.posted_on}</span>
                                    </div>
                                </div>
                                <InstagramCaption text={post?.platform === 'ig' ? (post?.ig?.caption ?? '') : post?.platform === 'yt' ? (post?.yt?.title ?? '') : ''} />
                            </div>
                        </PerfectScrollbar>
                        {
                            !preventAction && influencer &&
                            <>
                                {
                                    postStatus === 'pending' &&
                                    <React.Fragment>
                                        {
                                            actionConfirmation ? (
                                                <div className={`confirmation-bar w-100 d-flex justify-content-between align-items-center h-max-content mt-2 position-absolute bottom-0 px-3 ${actionConfirmation.action === 'reject' ? 'bg-danger' : 'bg-success'}`} onClick={e => e.stopPropagation()}>
                                                    <span>Are you sure, You want to {actionConfirmation.action} ?</span>
                                                    <div className="d-flex justify-content-end align-items-center gap-2">
                                                        <button className="confirmation-btn" onClick={actionConfirmation.onConfirmation}>
                                                            <i className='bx bx-check fs-4 text-success' />
                                                        </button>
                                                        <button className="confirmation-btn" onClick={e => setActionConfirmation(null)}>
                                                            <i className='bx bx-x fs-4 text-danger' />
                                                        </button>
                                                    </div>
                                                </div>
                                            ) :
                                                (
                                                    <div className="action-bar mt-2 position-absolute bottom-0">
                                                        <div className="d-flex jusitfy-content-between align-items-center w-100">
                                                            <button disabled={!privileges.includes(PRIVILEGE.CAMPAIGN.COLLABORATION.PROCESS)} className="theme-btn reject btn-light-danger w-100 action-btn" onClick={() => setActionConfirmation({ action: 'reject', onConfirmation: reject })}><i className='bx bx-x me-1 fs-4' ></i>Reject</button>
                                                            <button disabled={!privileges.includes(PRIVILEGE.CAMPAIGN.COLLABORATION.PROCESS)} className="theme-btn approve btn-light-success w-100 action-btn" onClick={() => setActionConfirmation({ action: 'approve', onConfirmation: approve })}><i className='bx bx-check me-1 fs-4' ></i>Approve</button>
                                                        </div>
                                                    </div>
                                                )
                                        }
                                    </React.Fragment>
                                }
                                {
                                    postStatus === 'processing' &&
                                    <div className="confirmation-bar d-flex justify-content-between align-items-center h-max-content mt-2 position-absolute bottom-0 w-100 px-3" onClick={e => e.stopPropagation()}>
                                        <span>Processing...</span>
                                        <Spinner size="sm" className='me-2'> Loading... </Spinner>
                                    </div>
                                }
                            </>
                        }

                    </div>

                </div>
            }
            {
                (!postId || !socialAccount) &&
                <span>No media found!</span>
            }

        </Modal>
    )
}

export default InfluencerPostModal